import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  ParkingCircle, Plus, Upload, Sparkles, Loader2, X, ArrowLeft, Trash2,
  Check, AlertCircle, ChevronDown, ChevronRight, FileSpreadsheet, Brain,
  Download, History, Clock, FileSpreadsheet as FileXlsx,
} from "lucide-react";
import { toast } from "sonner";
import CentroAutocomplete from "@/components/CentroAutocomplete";

const OK_NOK_OPTS = [
  { value: "",     label: "—",   cls: "bg-gray-100 text-gray-500" },
  { value: "OK",   label: "OK",  cls: "bg-emerald-100 text-emerald-700 font-bold" },
  { value: "NOK",  label: "NOK", cls: "bg-rose-100 text-rose-700 font-bold" },
  { value: "N/A",  label: "N/A", cls: "bg-amber-100 text-amber-700 font-semibold" },
];

export default function Arrive() {
  const { id } = useParams();
  return id ? <ArriveDetail id={id}/> : <ArriveList/>;
}

// ════════════════════════════════════════════════════════════════════════════
// LISTADO DE CHECKLISTS
// ════════════════════════════════════════════════════════════════════════════
function ArriveList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(null);  // "auto" | "wizard"

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/arrive/checklists`);
      setItems(r.data || []);
    } catch { toast.error("Error cargando checklists"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (cid) => {
    if (!window.confirm("¿Eliminar checklist completo?")) return;
    try {
      await axios.delete(`${API}/arrive/checklists/${cid}`);
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  return (
    <div className="space-y-5 w-full" data-testid="arrive-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
            <ParkingCircle className="text-[#007934]"/> Checklists ARRIVE
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pruebas de integración de la APP ARRIVE / MobilityHub por ciudad
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport("wizard")}
            data-testid="arrive-import-wizard-btn"
            title="Importar Excel con Pando — modo wizard (previsualización editable)"
            className="bg-white border-2 border-[#007934] text-[#007934] text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#007934]/5">
            <Brain size={14}/> Importar con Pando (Wizard)
          </button>
          <button
            onClick={() => setShowImport("auto")}
            data-testid="arrive-import-auto-btn"
            title="Importar Excel — la Pando infiere y crea el checklist en 1 click"
            className="bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#006528]">
            <Sparkles size={14}/> Importar con Pando (Auto)
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400"><Loader2 className="inline animate-spin mr-2" size={14}/> Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState onImport={() => setShowImport("wizard")}/>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(c => (
            <ChecklistCard key={c.id} c={c} onOpen={() => nav(`/arrive/${c.id}`)} onDelete={() => del(c.id)}/>
          ))}
        </div>
      )}

      {showImport && (
        <ImportModal
          mode={showImport}
          onClose={() => setShowImport(null)}
          onImported={() => { setShowImport(null); load(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onImport }) {
  return (
    <div className="bg-white border-2 border-dashed border-gray-200 rounded-xl p-12 text-center" data-testid="arrive-empty">
      <ParkingCircle size={32} className="text-gray-300 mx-auto mb-3"/>
      <p className="text-sm font-semibold text-gray-700">Sin checklists todavía</p>
      <p className="text-xs text-gray-500 mt-1">Importa un Excel de pruebas ARRIVE y la Pando construirá el checklist por ti.</p>
      <button onClick={onImport}
              className="mt-4 bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#006528]">
        <Upload size={13}/> Importar Excel
      </button>
    </div>
  );
}

function ChecklistCard({ c, onOpen, onDelete }) {
  const okPct = c.total_items ? (c.ok / c.total_items) * 100 : 0;
  const nokPct = c.total_items ? (c.nok / c.total_items) * 100 : 0;
  const naPct = c.total_items ? (c.na / c.total_items) * 100 : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#007934]/40 hover:shadow-md transition-all" data-testid={`arrive-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-900 truncate" title={c.ciudad}>{c.ciudad}</h3>
          <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5" title={c.descripcion}>
            {c.centro_nombre ? `${c.centro_cb} — ${c.centro_nombre}` : (c.descripcion || c.nombre)}
          </p>
        </div>
        <button onClick={onDelete} className="text-gray-300 hover:text-rose-500 p-1" title="Eliminar checklist">
          <Trash2 size={13}/>
        </button>
      </div>
      <div className="flex items-center gap-3 text-[11px] mb-2">
        <span className="font-bold text-gray-700">{c.total_items}</span>
        <span className="text-gray-500">items</span>
        <span className="ml-auto inline-flex items-center gap-1 bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded font-semibold">
          {c.completion_pct}% completado
        </span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
        {okPct > 0 && <div className="bg-emerald-500" style={{ width: `${okPct}%` }}/>}
        {nokPct > 0 && <div className="bg-rose-500" style={{ width: `${nokPct}%` }}/>}
        {naPct > 0 && <div className="bg-amber-400" style={{ width: `${naPct}%` }}/>}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 text-[10px] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full"/> {c.ok} OK</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 rounded-full"/> {c.nok} NOK</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-full"/> {c.na} N/A</span>
        </div>
        <button onClick={onOpen}
                data-testid={`arrive-open-${c.id}`}
                className="text-[11px] text-[#007934] font-semibold hover:underline inline-flex items-center gap-1">
          Abrir <ChevronRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL DE IMPORTACIÓN  (auto | wizard)
// ════════════════════════════════════════════════════════════════════════════
function ImportModal({ mode, onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // wizard mode

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("Sube un .xlsx o .xls");
      return;
    }
    setFile(f);
  };

  const runAuto = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/arrive/import-excel/auto`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Checklist creado: ${r.data.ciudad} · ${(r.data.items||[]).length} items`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally { setBusy(false); }
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/arrive/import-excel/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data.preview);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando preview");
    } finally { setBusy(false); }
  };

  const confirmPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/arrive/import-excel/confirm`, {
        ciudad: preview.ciudad,
        nombre: preview.nombre,
        descripcion: preview.descripcion,
        centro_cb: preview.centro_cb || null,
        centro_nombre: preview.centro_nombre || null,
        items: preview.items,
      });
      toast.success(`Checklist creado: ${r.data.ciudad}`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error confirmando");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="arrive-import-modal">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              {mode === "auto" ? <Sparkles size={16} className="text-[#007934]"/> : <Brain size={16} className="text-[#007934]"/>}
              Importar Excel · Modo {mode === "auto" ? "Automático" : "Wizard"}
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {mode === "auto"
                ? "La Pando infiere ciudad, categorías e items y crea el checklist en un click."
                : "La Pando propone una previsualización editable antes de guardar."}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!preview ? (
            <div className="space-y-3">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 hover:border-[#007934] rounded-xl p-8 text-center cursor-pointer transition-colors">
                <FileSpreadsheet size={28} className="text-gray-300 mx-auto mb-2"/>
                {file ? (
                  <p className="text-sm font-semibold text-gray-700">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-gray-700">Haz clic para subir un .xlsx</p>
                    <p className="text-[11px] text-gray-400 mt-1">Excel con la batería de pruebas ARRIVE de una ciudad</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onPick}
                  className="hidden"
                  data-testid="arrive-file-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={onClose}
                        className="text-xs text-gray-600 px-3 py-2 rounded border border-gray-200 hover:bg-gray-50">Cancelar</button>
                <button
                  onClick={mode === "auto" ? runAuto : runPreview}
                  disabled={!file || busy}
                  data-testid="arrive-run-btn"
                  className="bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1.5">
                  {busy ? <Loader2 size={13} className="animate-spin"/> :
                    (mode === "auto" ? <Sparkles size={13}/> : <Brain size={13}/>)}
                  {busy ? "La Pando está procesando…" : (mode === "auto" ? "Importar ya" : "Generar preview con Pando")}
                </button>
              </div>
            </div>
          ) : (
            <PreviewEditor preview={preview} setPreview={setPreview} onConfirm={confirmPreview} busy={busy}/>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewEditor({ preview, setPreview, onConfirm, busy }) {
  const updField = (k, v) => setPreview(p => ({ ...p, [k]: v }));
  const updItem = (idx, k, v) =>
    setPreview(p => ({ ...p, items: p.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
  const removeItem = (idx) =>
    setPreview(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-3">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px] text-emerald-800 flex items-start gap-2">
        <Brain size={14} className="text-emerald-600 mt-0.5 flex-shrink-0"/>
        <div>
          <strong>Pando · Claude Sonnet 4.6</strong> ha generado esta propuesta a partir de tu Excel.
          Revisa, ajusta lo que necesites y pulsa <em>Confirmar y crear</em>.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-gray-600 font-semibold">
          Ciudad
          <input value={preview.ciudad || ""} onChange={e => updField("ciudad", e.target.value)}
                 className="w-full mt-1 text-xs border border-gray-200 rounded px-2 py-1.5"
                 data-testid="preview-ciudad"/>
        </label>
        <label className="text-[11px] text-gray-600 font-semibold">
          Nombre del checklist
          <input value={preview.nombre || ""} onChange={e => updField("nombre", e.target.value)}
                 className="w-full mt-1 text-xs border border-gray-200 rounded px-2 py-1.5"/>
        </label>
      </div>
      <label className="text-[11px] text-gray-600 font-semibold block">
        Descripción
        <textarea value={preview.descripcion || ""} onChange={e => updField("descripcion", e.target.value)}
                  rows={2}
                  className="w-full mt-1 text-xs border border-gray-200 rounded px-2 py-1.5"/>
      </label>
      <label className="text-[11px] text-gray-600 font-semibold block">
        Centro (CeBe SAP)
        <CentroAutocomplete value={preview.centro_cb ? `${preview.centro_cb} — ${preview.centro_nombre}` : ""}
          testId="arrive-preview-centro"
          onSelect={(c) => { updField("centro_cb", c?.codigo || ""); updField("centro_nombre", c?.descripcion || ""); }} />
      </label>

      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-600 flex items-center justify-between">
          <span>{preview.items?.length || 0} items propuestos</span>
        </div>
        <div className="divide-y divide-gray-100 max-h-[40vh] overflow-y-auto">
          {(preview.items || []).map((it, idx) => (
            <div key={idx} className="p-2.5 flex items-start gap-2 hover:bg-gray-50">
              <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-12 flex-shrink-0">{it.codigo || "—"}</span>
              <div className="flex-1 min-w-0">
                <input value={it.titulo || ""} onChange={e => updItem(idx, "titulo", e.target.value)}
                       className="w-full text-xs font-semibold border-none outline-none bg-transparent focus:bg-white focus:ring-1 focus:ring-[#007934] rounded px-1"/>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">{it.categoria || "Sin categoría"}</span>
                  {it.subcategoria && <span className="ml-1 text-gray-400">· {it.subcategoria}</span>}
                </p>
              </div>
              <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-rose-500 p-1" title="Quitar">
                <X size={12}/>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button onClick={() => setPreview(null)} className="text-xs text-gray-600 px-3 py-2 rounded border border-gray-200 hover:bg-gray-50">
          Volver
        </button>
        <button onClick={onConfirm} disabled={busy}
                data-testid="arrive-confirm-btn"
                className="bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
          Confirmar y crear
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DETALLE DEL CHECKLIST
// ════════════════════════════════════════════════════════════════════════════
function ArriveDetail({ id }) {
  const nav = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});  // {categoryKey: true}
  const [showFill, setShowFill] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/arrive/checklists/${id}/export.xlsx`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arrive_${checklist?.slug || id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado correctamente");
    } catch {
      toast.error("Error al exportar Excel");
    } finally { setExporting(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/arrive/checklists/${id}`);
      setChecklist(r.data);
    } catch { toast.error("Error cargando checklist"); }
    finally { setLoading(false); }
  };
  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/arrive/checklists/${id}/import-history`);
      setHistory(r.data?.history || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadHistory(); }, [id]);

  const updateItem = async (itemId, changes) => {
    try {
      const r = await axios.put(`${API}/arrive/checklists/${id}/items/${itemId}`, changes);
      setChecklist(c => ({
        ...c,
        items: c.items.map(it => it.id === itemId ? { ...it, ...r.data } : it),
      }));
    } catch { toast.error("Error actualizando item"); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400"><Loader2 className="inline animate-spin mr-2" size={14}/> Cargando…</div>;
  if (!checklist) return null;

  // Agrupar items por categoría
  const groups = {};
  for (const it of checklist.items || []) {
    const cat = it.categoria || "Sin categoría";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(it);
  }
  const total = checklist.items?.length || 0;
  const ok = checklist.items?.filter(i => i.ok_nok === "OK").length || 0;
  const nok = checklist.items?.filter(i => i.ok_nok === "NOK").length || 0;
  const na = checklist.items?.filter(i => i.ok_nok === "N/A").length || 0;
  const pct = total ? Math.round((ok + nok + na) / total * 100) : 0;

  return (
    <div className="space-y-4 w-full" data-testid="arrive-detail">
      <button onClick={() => nav("/arrive")} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
        <ArrowLeft size={12}/> Volver al listado
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
              <ParkingCircle className="text-[#007934]"/> {checklist.ciudad}
            </h1>
            <p className="text-sm text-gray-600 mt-1">{checklist.nombre}</p>
            <p className="text-xs text-gray-500 mt-2">{checklist.descripcion}</p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowFill(true)}
                data-testid="arrive-fill-btn"
                title="Sube un Excel con OK/NOK + Fecha + Observaciones y la plataforma rellenará los items que coincidan por código"
                className="bg-white border-2 border-[#007934] text-[#007934] text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#007934]/5">
                <Upload size={12}/> Importar resultados desde Excel
              </button>
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                data-testid="arrive-export-btn"
                title="Descargar checklist completo con resultados en Excel"
                className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#006528] disabled:opacity-60 transition-colors">
                {exporting ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>}
                Exportar Excel
              </button>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-[#007934]" data-testid="arrive-completion">{pct}%</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Completado</div>
            <div className="flex items-center gap-2 mt-2 text-[10px]">
              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">{ok} OK</span>
              <span className="bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold">{nok} NOK</span>
              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">{na} N/A</span>
              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-semibold">{total - ok - nok - na} pdte</span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mini-historial de importaciones ─────────────────────────────── */}
      <ImportHistoryPanel
        history={history}
        expanded={showHistory}
        onToggle={() => setShowHistory(s => !s)}
      />

      <div className="space-y-3">
        {Object.entries(groups).map(([cat, its]) => {
          const isCol = collapsed[cat];
          const catOk = its.filter(i => i.ok_nok === "OK").length;
          const catTotal = its.length;
          return (
            <div key={cat} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 text-left">
                {isCol ? <ChevronRight size={14}/> : <ChevronDown size={14}/>}
                <span className="text-sm font-bold text-gray-800 flex-1">{cat}</span>
                <span className="text-[11px] text-gray-500">{catOk}/{catTotal}</span>
              </button>
              {!isCol && (
                <div className="divide-y divide-gray-100">
                  {its.map(it => <ItemRow key={it.id} it={it} onChange={(c) => updateItem(it.id, c)}/>)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showFill && (
        <FillFromExcelModal
          cid={id}
          onClose={() => setShowFill(false)}
          onApplied={() => { setShowFill(false); load(); loadHistory(); }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PANEL: HISTORIAL DE IMPORTACIONES (mini-timeline + sección colapsable)
// ════════════════════════════════════════════════════════════════════════════
const IMPORT_KIND_LABEL = {
  create_auto:     "Auto (Pando)",
  create_wizard:   "Wizard",
  fill_from_excel: "Resultados",
};
const IMPORT_KIND_CLS = {
  create_auto:     "bg-violet-100 text-violet-700",
  create_wizard:   "bg-indigo-100 text-indigo-700",
  fill_from_excel: "bg-emerald-100 text-emerald-700",
};

function _fmtImportDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ImportHistoryPanel({ history, expanded, onToggle }) {
  const last = history && history.length ? history[0] : null;

  if (!history || history.length === 0) {
    return (
      <div
        className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-2.5 text-[11px] text-gray-500 flex items-center gap-2"
        data-testid="arrive-import-history-empty"
      >
        <History size={13} className="text-gray-400"/>
        Aún no se ha registrado ninguna importación para este checklist.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#007934]/20 bg-gradient-to-r from-[#007934]/5 to-emerald-50 overflow-hidden" data-testid="arrive-import-history">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/40 transition-colors"
        data-testid="arrive-import-history-toggle"
      >
        <Clock size={14} className="text-[#007934] shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="font-semibold text-gray-800">Último import:</span>
            <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${IMPORT_KIND_CLS[last.kind] || "bg-gray-100 text-gray-700"}`}>
              {IMPORT_KIND_LABEL[last.kind] || last.kind}
            </span>
            <span className="text-gray-700 truncate" title={last.filename}>
              <strong>{last.filename || "(sin archivo)"}</strong>
            </span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-600">{_fmtImportDate(last.at)}</span>
            <span className="text-gray-500">·</span>
            <span className="text-gray-600 truncate" title={last.by}>{last.by || "—"}</span>
            <span className="text-gray-500">·</span>
            <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold text-[10px]">
              {last.changes} cambio{last.changes === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <span className="text-[11px] text-[#007934] font-semibold inline-flex items-center gap-1 shrink-0">
          {expanded ? "Ocultar" : `Ver todos (${history.length})`}
          {expanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-[#007934]/15 bg-white/70 px-4 py-3" data-testid="arrive-import-history-list">
          <ol className="relative border-l border-gray-200 ml-2 space-y-3">
            {history.map((h) => (
              <li key={h.id || h.at} className="ml-3">
                <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-[#007934] ring-2 ring-white"/>
                <div className="text-[11px] flex items-center gap-2 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${IMPORT_KIND_CLS[h.kind] || "bg-gray-100 text-gray-700"}`}>
                    {IMPORT_KIND_LABEL[h.kind] || h.kind}
                  </span>
                  <FileXlsx size={12} className="text-gray-400"/>
                  <span className="font-semibold text-gray-800 truncate max-w-[280px]" title={h.filename}>
                    {h.filename || "(sin archivo)"}
                  </span>
                  <span className="text-gray-500">· {_fmtImportDate(h.at)}</span>
                  <span className="text-gray-500">· {h.by || "—"}</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="text-emerald-700 font-semibold">{h.changes} cambio{h.changes === 1 ? "" : "s"}</span>
                  {h.items_total != null && <span>· {h.items_total} items</span>}
                  {h.rows_in_excel != null && <span>· {h.rows_in_excel} filas Excel</span>}
                  {h.not_found_total ? <span className="text-amber-700">· {h.not_found_total} no encontrados</span> : null}
                  {h.ciudad ? <span>· {h.ciudad}</span> : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL: IMPORTAR RESULTADOS DESDE EXCEL (rellena items existentes por código)
// ════════════════════════════════════════════════════════════════════════════
function FillFromExcelModal({ cid, onClose, onApplied }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);  // {matched, not_found, changes…}

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) { toast.error("Sube un .xlsx o .xls"); return; }
    setFile(f); setPreview(null);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(
        `${API}/arrive/checklists/${cid}/fill-from-excel?dry_run=true`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando preview");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(
        `${API}/arrive/checklists/${cid}/fill-from-excel?dry_run=false`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.matched} item${r.data.matched === 1 ? "" : "s"} actualizados`);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error aplicando");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="arrive-fill-modal">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Upload size={16} className="text-[#007934]"/> Importar resultados desde Excel
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              La plataforma matchea las filas del Excel por <strong>código</strong> (2.1, 2.2…) y
              rellena <strong>OK/NOK</strong>, <strong>Fecha</strong> y <strong>Observaciones</strong> en los items existentes.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {/* selector */}
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-200 hover:border-[#007934] rounded-xl p-6 text-center cursor-pointer">
            <FileSpreadsheet size={26} className="text-gray-300 mx-auto mb-2"/>
            {file ? (
              <p className="text-sm font-semibold text-gray-700">{file.name}</p>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700">Sube el Excel con los resultados</p>
                <p className="text-[11px] text-gray-400 mt-1">Estructura típica ARRIVE: columna Id + OK/NOK + FECHA + Observaciones</p>
              </>
            )}
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPick} className="hidden" data-testid="arrive-fill-file"/>
          </div>

          {!preview && file && (
            <div className="flex items-center justify-end">
              <button
                onClick={runPreview}
                disabled={busy}
                data-testid="arrive-fill-preview-btn"
                className="bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 flex items-center gap-1.5">
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Brain size={13}/>}
                Previsualizar cambios
              </button>
            </div>
          )}

          {preview && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  <p className="text-[10px] text-emerald-700 font-semibold uppercase">Cambios detectados</p>
                  <p className="text-2xl font-bold text-emerald-600">{preview.matched}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <p className="text-[10px] text-amber-700 font-semibold uppercase">Sin match</p>
                  <p className="text-2xl font-bold text-amber-600">{preview.not_found_total || 0}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-[10px] text-gray-600 font-semibold uppercase">Filas leídas</p>
                  <p className="text-2xl font-bold text-gray-700">{preview.rows_in_excel || 0}</p>
                </div>
              </div>

              {preview.changes?.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-[40vh] overflow-y-auto">
                  <div className="bg-gray-50 px-3 py-2 text-[11px] font-semibold text-gray-600">
                    Cambios a aplicar
                  </div>
                  <div className="divide-y divide-gray-100">
                    {preview.changes.map((ch, idx) => (
                      <div key={idx} className="p-2.5" data-testid={`fill-change-${ch.codigo}`}>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] text-gray-400 font-mono w-10">{ch.codigo}</span>
                          <p className="text-xs font-semibold text-gray-800 truncate flex-1" title={ch.titulo}>{ch.titulo}</p>
                        </div>
                        <div className="ml-12 mt-1 space-y-0.5">
                          {Object.entries(ch.after).map(([k, v]) => (
                            <div key={k} className="text-[10px] flex items-center gap-1.5">
                              <span className="text-gray-400">{k}:</span>
                              {ch.before[k] && (
                                <>
                                  <span className="line-through text-gray-400">{String(ch.before[k]).slice(0, 40)}</span>
                                  <span className="text-gray-400">→</span>
                                </>
                              )}
                              <span className="font-semibold text-emerald-700">{String(v).slice(0, 80)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.not_found?.length > 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <strong>Códigos sin match (no existen en este checklist):</strong>{" "}
                  {preview.not_found.join(", ")}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => { setPreview(null); setFile(null); }}
                        className="text-xs text-gray-600 px-3 py-2 rounded border border-gray-200 hover:bg-gray-50">
                  Cancelar
                </button>
                <button
                  onClick={apply}
                  disabled={busy || preview.matched === 0}
                  data-testid="arrive-fill-apply-btn"
                  className="bg-[#007934] text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-40 flex items-center gap-1.5">
                  {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
                  Aplicar {preview.matched} cambio{preview.matched === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({ it, onChange }) {
  const okCfg = OK_NOK_OPTS.find(o => o.value === (it.ok_nok || "")) || OK_NOK_OPTS[0];
  return (
    <div className="p-3 hover:bg-gray-50" data-testid={`arrive-item-${it.id}`}>
      <div className="flex items-start gap-3">
        <span className="text-[10px] text-gray-400 font-mono mt-0.5 w-12 flex-shrink-0">{it.codigo || "—"}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{it.titulo}</p>
          {it.subcategoria && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">{it.subcategoria}</span>
            </p>
          )}
          {it.descripcion && <p className="text-[11px] text-gray-600 whitespace-pre-line mt-1">{it.descripcion}</p>}
          {it.resultado_esperado && (
            <p className="text-[11px] text-emerald-700 mt-1 whitespace-pre-line">
              <strong>Resultado esperado:</strong> {it.resultado_esperado}
            </p>
          )}
          {it.id_equipo && <p className="text-[10px] text-gray-400 mt-1">Equipo: {it.id_equipo}</p>}
        </div>
        <div className="flex flex-col gap-1.5 w-[230px] flex-shrink-0">
          <div className="flex items-center gap-1">
            {OK_NOK_OPTS.filter(o => o.value).map(o => (
              <button
                key={o.value}
                onClick={() => onChange({ ok_nok: it.ok_nok === o.value ? null : o.value })}
                data-testid={`arrive-ok-${it.id}-${o.value}`}
                className={`text-[10px] px-2 py-1 rounded flex-1 ${it.ok_nok === o.value ? o.cls : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            value={it.fecha || ""}
            onChange={e => onChange({ fecha: e.target.value || null })}
            className="text-[11px] border border-gray-200 rounded px-1.5 py-1"
            data-testid={`arrive-date-${it.id}`}/>
          <textarea
            value={it.observaciones || ""}
            onChange={e => onChange({ observaciones: e.target.value })}
            placeholder="Observaciones…"
            rows={2}
            className="text-[11px] border border-gray-200 rounded px-1.5 py-1 resize-none"
            data-testid={`arrive-obs-${it.id}`}/>
        </div>
      </div>
    </div>
  );
}
